function lockPointer(cursor) {
  document.body.style.cursor = cursor;
  document.body.style.userSelect = 'none';
}

function unlockPointer() {
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
}

function clamp(value, min, max) {
  return Math.round(Math.max(min, Math.min(max, value)));
}

function listen(target, event, handler, opts) {
  target.addEventListener(event, handler, opts);
  return () => target.removeEventListener(event, handler);
}

function getSheetPadding() {
  return parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--spacing-sheet')) || 8;
}

export default {
  Resizable: {
    mounted() {
      this.interacting = false;
      this._cleanups = [];
      this.initialize();
    },

    updated() {
      const nextMode = this.el.dataset.mode || 'sidebar';
      if (nextMode === this.mode) return;
      this.teardown();
      this.initialize();
    },

    destroyed() {
      this.teardown();
    },

    initialize() {
      this.mode = this.el.dataset.mode || 'sidebar';
      if (this.mode === 'sidebar') {
        this.initSidebar();
      } else {
        this.initFloating();
      }
    },

    teardown() {
      this._cleanups.forEach(fn => fn());
      this._cleanups = [];
    },

    // --- Sidebar mode (width resize via right-edge handle) ---

    initSidebar() {
      this.minWidth = parseInt(this.el.dataset.minWidth || '240', 10);
      this.maxWidth = parseInt(this.el.dataset.maxWidth || '800', 10);

      const handle = this.el.querySelector('[data-resize-handle]');
      const line = this.el.querySelector('[data-resize-line]');
      if (!handle) return;

      this._cleanups.push(
        listen(handle, 'pointerdown', e => {
          e.preventDefault();
          this.startSidebarResize(e, line);
        }),
      );
    },

    startSidebarResize(e, line) {
      this.interacting = true;
      const startX = e.pageX;
      const startWidth = this.el.offsetWidth;

      if (line) {
        line.style.backgroundColor = 'var(--color-indigo-500)';
        line.style.width = '2px';
      }

      lockPointer('col-resize');

      const onMove = e => {
        if (!this.interacting) return;
        const delta = e.pageX - startX;
        this.el.style.width = clamp(startWidth + delta, this.minWidth, this.maxWidth) + 'px';
      };

      const cleanup = listen(document, 'pointermove', onMove);

      listen(
        document,
        'pointerup',
        () => {
          this.interacting = false;
          cleanup();
          unlockPointer();
          if (line) {
            line.style.backgroundColor = '';
            line.style.width = '';
          }
          this.pushEventTo(this.el, 'resize_ai_sidebar', { width: this.el.offsetWidth });
        },
        { once: true },
      );
    },

    // --- Floating mode (resize from left/top/corner + drag via header) ---

    initFloating() {
      this.minWidth = parseInt(this.el.dataset.minWidth || '280', 10);
      this.maxWidth = parseInt(this.el.dataset.maxWidth || '600', 10);
      this.minHeight = parseInt(this.el.dataset.minHeight || '300', 10);
      this.maxHeight = parseInt(this.el.dataset.maxHeight || '800', 10);

      const handleLeft = this.el.querySelector('[data-resize-handle="left"]');
      const handleRight = this.el.querySelector('[data-resize-handle="right"]');
      const handleTop = this.el.querySelector('[data-resize-handle="top"]');
      const handleTopLeft = this.el.querySelector('[data-resize-handle="top_left"]');
      const handleTopRight = this.el.querySelector('[data-resize-handle="top_right"]');
      const dragHandle = this.el.querySelector('[data-drag-handle]');

      if (handleLeft) {
        this._cleanups.push(
          listen(handleLeft, 'pointerdown', e => {
            e.preventDefault();
            this.startFloatingResize(e, handleLeft, { resizeWidth: true, fromLeft: true });
          }),
        );
      }

      if (handleRight) {
        this._cleanups.push(
          listen(handleRight, 'pointerdown', e => {
            e.preventDefault();
            this.startFloatingResize(e, handleRight, { resizeWidth: true, fromLeft: false });
          }),
        );
      }

      if (handleTop) {
        this._cleanups.push(
          listen(handleTop, 'pointerdown', e => {
            e.preventDefault();
            this.startFloatingResize(e, handleTop, { resizeHeight: true });
          }),
        );
      }

      if (handleTopLeft) {
        this._cleanups.push(
          listen(handleTopLeft, 'pointerdown', e => {
            e.preventDefault();
            this.startFloatingResize(e, handleTopLeft, { resizeWidth: true, resizeHeight: true, fromLeft: true });
          }),
        );
      }

      if (handleTopRight) {
        this._cleanups.push(
          listen(handleTopRight, 'pointerdown', e => {
            e.preventDefault();
            this.startFloatingResize(e, handleTopRight, { resizeWidth: true, resizeHeight: true, fromLeft: false });
          }),
        );
      }

      if (dragHandle) {
        this._cleanups.push(
          listen(dragHandle, 'pointerdown', e => {
            if (e.target.closest('button, [data-no-drag]')) return;
            e.preventDefault();
            this.startDrag(e, dragHandle);
          }),
        );
      }
    },

    startFloatingResize(e, handle, { resizeWidth = false, resizeHeight = false, fromLeft = true }) {
      this.interacting = true;
      const startX = e.pageX;
      const startY = e.pageY;
      const startWidth = this.el.offsetWidth;
      const startHeight = this.el.offsetHeight;
      const startRight = parseInt(this.el.style.right, 10) || 24;

      const line = handle.querySelector('[data-resize-line]');
      if (line) {
        line.style.backgroundColor = 'var(--color-indigo-500)';
        if (resizeWidth && !resizeHeight) line.style.width = '2px';
        if (resizeHeight && !resizeWidth) line.style.height = '2px';
      }

      const cursorMap = {
        true_true: fromLeft ? 'nw-resize' : 'ne-resize',
        true_false: 'col-resize',
        false_true: 'row-resize',
      };
      lockPointer(cursorMap[`${resizeWidth}_${resizeHeight}`] || 'default');

      const sheetPad = getSheetPadding();

      const onMove = e => {
        if (!this.interacting) return;

        if (resizeWidth) {
          if (fromLeft) {
            const deltaX = startX - e.pageX;
            const newWidth = clamp(startWidth + deltaX, this.minWidth, this.maxWidth);
            this.el.style.width = newWidth + 'px';
          } else {
            const deltaX = e.pageX - startX;
            const newWidth = clamp(startWidth + deltaX, this.minWidth, this.maxWidth);
            const widthDiff = newWidth - startWidth;
            this.el.style.width = newWidth + 'px';
            this.el.style.right = Math.max(sheetPad, startRight - widthDiff) + 'px';
          }
        }

        if (resizeHeight) {
          const deltaY = startY - e.pageY;
          const newHeight = clamp(startHeight + deltaY, this.minHeight, this.maxHeight);
          this.el.style.height = newHeight + 'px';
        }
      };

      const cleanup = listen(document, 'pointermove', onMove);

      listen(
        document,
        'pointerup',
        () => {
          this.interacting = false;
          cleanup();
          unlockPointer();
          if (line) {
            line.style.backgroundColor = '';
            line.style.width = '';
            line.style.height = '';
          }
          this.persistFloating();
        },
        { once: true },
      );
    },

    startDrag(e, dragHandle) {
      this.interacting = true;
      const startX = e.pageX;
      const startRight = parseInt(this.el.style.right, 10) || 24;
      const panelWidth = this.el.offsetWidth;

      dragHandle.style.cursor = 'grabbing';
      lockPointer('grabbing');

      const sheetPadding = getSheetPadding();
      const onMove = e => {
        if (!this.interacting) return;
        const deltaX = startX - e.pageX;
        const maxRight = window.innerWidth - panelWidth - sheetPadding;
        this.el.style.right = clamp(startRight + deltaX, sheetPadding, maxRight) + 'px';
      };

      const cleanup = listen(document, 'pointermove', onMove);

      listen(
        document,
        'pointerup',
        () => {
          this.interacting = false;
          cleanup();
          unlockPointer();
          dragHandle.style.cursor = '';
          this.persistFloating();
        },
        { once: true },
      );
    },

    persistFloating() {
      this.pushEventTo(this.el, 'resize_ai_floating', {
        width: Math.round(this.el.offsetWidth),
        height: Math.round(this.el.offsetHeight),
        right: Math.round(parseInt(this.el.style.right, 10) || 24),
      });
    },
  },
};
